import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  InputLabel,
  Select,
  SelectPlaceholder,
  MenuItem,
  FormHelperText,
  CircularProgress,
  useQuickCreateMenuPaper,
} from '@/shared/ui';
import { usePermission } from '@/shared/hooks/usePermission';
import { queryKeys } from '@/shared/query';
import {
  fetchCustomerSources,
  type CustomerSource,
} from '@/modules/patient/api/customerSource.api';
import CustomerSourceFormDialog from './CustomerSourceFormDialog';

interface CustomerSourceSelectProps {
  value: string;
  onChange: (sourceId: string) => void;
  disabled?: boolean;
  error?: string | null;
  label?: string;
  required?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
  /** Include inactive sources when editing a record that already uses one. */
  includeInactiveIds?: string[];
}

/** Same slot as the dropdown arrow, so the field keeps its height while the catalog loads. */
const CustomerSourceLoadingIcon = ({ className }: { className?: string }) => (
  <CircularProgress className={className} size={16} />
);

const selectIconSx = {
  '&:not(:hover):not(:focus-within) .MuiSelect-icon': { display: 'none' },
  '&:not(:hover):not(:focus-within) .MuiSelect-select': { pr: '8px !important' },
} as const;

const CustomerSourceSelect: React.FC<CustomerSourceSelectProps> = ({
  value,
  onChange,
  disabled = false,
  error,
  label,
  required = false,
  allowEmpty = true,
  emptyLabel,
  includeInactiveIds = [],
}) => {
  const { t } = useTranslation(['patient', 'common']);
  const { hasPermission } = usePermission();
  const [createOpen, setCreateOpen] = useState(false);
  const needsInactive = includeInactiveIds.length > 0;
  const canCreate = hasPermission('customer-sources:create') && !disabled;
  const quickCreate = useQuickCreateMenuPaper({
    canCreate,
    createLabel: t('customerSourceCatalog.actions.add'),
    onQuickCreate: () => setCreateOpen(true),
  });

  const sourcesQuery = useQuery({
    queryKey: queryKeys.patient.customerSources({ includeInactive: needsInactive }),
    queryFn: () => fetchCustomerSources(needsInactive),
  });

  const includeInactiveKey = includeInactiveIds.join(',');
  const sources = useMemo(() => {
    const inactiveIds = includeInactiveKey ? includeInactiveKey.split(',') : [];
    const all = sourcesQuery.data ?? [];
    const active = all.filter((item) => item.isActive);
    const extras = all.filter(
      (item) => !item.isActive && inactiveIds.includes(item.id)
    );
    const byId = new Map<string, CustomerSource>();
    [...active, ...extras].forEach((item) => byId.set(item.id, item));
    return Array.from(byId.values()).sort((a, b) => a.sortOrder - b.sortOrder);
  }, [sourcesQuery.data, includeInactiveKey]);

  const loading = sourcesQuery.isPending;
  const resolvedLabel = label ?? t('fields.customerSource');
  const resolvedEmptyLabel = emptyLabel ?? t('fallbacks.notAvailable');

  return (
    <>
      <FormControl
        fullWidth
        required={required}
        error={Boolean(error)}
        disabled={disabled || loading}
        sx={loading ? undefined : selectIconSx}
      >
        <InputLabel required={required}>{resolvedLabel}</InputLabel>
        <Select
          value={value}
          label={resolvedLabel}
          IconComponent={loading ? CustomerSourceLoadingIcon : undefined}
          displayEmpty={allowEmpty}
          notched={allowEmpty || undefined}
          open={canCreate ? quickCreate.menuOpen : undefined}
          onOpen={canCreate ? quickCreate.onMenuOpen : undefined}
          onClose={canCreate ? quickCreate.onMenuClose : undefined}
          MenuProps={
            canCreate
              ? { slotProps: { paper: quickCreate.selectMenuPaperSlotProps } }
              : undefined
          }
          renderValue={
            allowEmpty
              ? (selected) => {
                  if (!selected) {
                    return <SelectPlaceholder>{resolvedEmptyLabel}</SelectPlaceholder>;
                  }
                  const source = sources.find((item) => item.id === selected);
                  if (!source) return String(selected);
                  return !source.isActive
                    ? `${source.name} (${t('customerSourceCatalog.status.inactive')})`
                    : source.name;
                }
              : undefined
          }
          onChange={(e) => onChange(String(e.target.value))}
        >
          {allowEmpty && <MenuItem value="">{resolvedEmptyLabel}</MenuItem>}
          {sources.map((source) => (
            <MenuItem key={source.id} value={source.id}>
              {source.name}
              {!source.isActive ? ` (${t('customerSourceCatalog.status.inactive')})` : ''}
            </MenuItem>
          ))}
        </Select>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>

      {canCreate ? (
        <CustomerSourceFormDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(source) => onChange(source.id)}
        />
      ) : null}
    </>
  );
};

export default CustomerSourceSelect;
